<script setup lang="ts">
import type { MysteryBoxItem } from '@/types/mystery-box'
import { computed, ref, watch } from 'vue'
import { ECOMMERCE_ITEM_TAG, type EcommerceItem } from '@/types/shared/player-config'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import MysteryBoxPurchaseEcommerceItem from '@/components/MysteryBoxPurchaseEcommerceItem.vue'
import { useShop } from '@/composables/game/shop'
import DefaultBadge from '@/components/periodic/DefaultBadge.vue'
import type { BadgeVariant } from '@/components/periodic/DefaultBadge.vue'

const props = defineProps<{
	show: boolean
	item?: MysteryBoxItem | null
}>()
defineEmits<{
	(e: 'hide'): void
	(e: 'onPurchase', item: EcommerceItem): void
}>()

const { isDefaultBOGO, isBOGO, isBOGX, isDefaultBOGX, saleValue } = useShop()

const ecommerceItems = computed((): EcommerceItem[] => props.item?.ecommerceItems ?? [])
const x1Item = computed(() => ecommerceItems.value.find(item => item.id.endsWith('X1')) ?? null)
const x3Item = computed(() => ecommerceItems.value.find(item => item.id.endsWith('X3')) ?? null)
const x5Item = computed(() => ecommerceItems.value.find(item => item.id.endsWith('X5')) ?? null)
const selectedEcommerceItem = ref<EcommerceItem | null>(null)

const canHaveSale = computed(() => ecommerceItems.value.some((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT)))
const hasBOGO = computed(() => canHaveSale.value && isBOGO.value)
const hasDefaultBOGO = computed(() => hasBOGO.value && isDefaultBOGO.value)

const saleVariant = computed<BadgeVariant>(() => {
  if (isDefaultBOGO.value) return 'BOGO'
  if (isDefaultBOGX.value) return 'BOGX'
  return 'discount' // fallback
})

watch(() => props.show, show => {
  if (!show) {
    selectedEcommerceItem.value = null
  }
})
</script>

<template>
  <TheBottomSheet
    :show="show && !!item"
    :back-button="!!selectedEcommerceItem"
    content-class="pt-6 px-6 pb-8"
    @hide="$emit('hide')"
    @back="selectedEcommerceItem = null"
  >
    <div
      v-if="item"
      class="flex-1 flex flex-col items-center relative"
    >
      <template v-if="!selectedEcommerceItem">
        <div class="relative">
          <img
            class="size-[160px] object-cover"
            :src="item.image"
          >
          <DefaultBadge
            v-if="hasDefaultBOGO || isDefaultBOGX"
            :variant="saleVariant"
            class="absolute left-4 top-4 w-[55px] h-auto z-[3]"
          />
        </div>

        <div class="mt-6 text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
          {{ item.name }}
        </div>
        <div class="text-center max-w-[328px] text-white/60 text-base font-medium">
          <T
            key-name="mystery_box.how_many_boxes_to_unlock"
            default-value="How many boxes do you want to unlock today?"
          />
        </div>
        <div class="mt-12 flex items-center gap-x-3 w-full min-w-0">
          <div class="flex-1">
            <BaseButton
              v-if="x1Item"
              class="w-full !px-0"
              :button-class="hasDefaultBOGO || isDefaultBOGX ? 'default-BOGO-background-buttons border-0' : ''"
              :text-class="hasDefaultBOGO || isDefaultBOGX ? 'text-tropee-black' : ''"
              @click="selectedEcommerceItem = x1Item"
            >
              <div class="flex items-center gap-x-2 relative max-[390px]:text-base max-[390px]:gap-x-1.5">
                <span
                  class="lowercase"
                  :class="(hasBOGO || isBOGX) && 'text-base line-through text-tropee-black/[66%]'"
                >x1</span>
                <span
                  v-if="hasBOGO || isBOGX"
                  class="absolute left-0 -rotate-[23deg] black-stroke-golden-text text-lg bottom-0 translate-y-1/2 lowercase"
                >x{{ hasBOGO ? 2 : saleValue }}</span>
                <div
                  class="
                  w-px
                  h-4
                  bg-tropee-black
                  [.24]"
                />
                <span class="!font-semibold">${{ x1Item.prices[0].usdAmount }}</span>
              </div>
            </BaseButton>
          </div>
          <div class="flex-1">
            <BaseButton
              v-if="x3Item"
              class="w-full !px-0"
              :button-class="hasDefaultBOGO ? 'default-BOGO-background-buttons border-0' : (isDefaultBOGX ? 'default-BOGX-background-buttons border-0' : '')"
              :text-class="hasDefaultBOGO || isDefaultBOGX ? 'text-[#1D1D1B]':''"
              @click="selectedEcommerceItem = x3Item"
            >
              <div class="flex items-center gap-x-2 relative max-[390px]:text-base max-[390px]:gap-x-1.5">
                <span
                  class="lowercase"
                  :class="(hasBOGO || isBOGX) && 'text-base line-through text-tropee-black/[66%]'"
                >x3</span>
                <span
                  v-if="hasBOGO || isBOGX"
                  class="absolute left-0 -rotate-[23deg] black-stroke-golden-text text-lg bottom-0 translate-y-1/2 lowercase"
                >x{{ hasBOGO ? 6 : 3 * saleValue }}</span>
                <div class="w-px h-4 bg-tropee-black/[.24]" />
                <span class="!font-semibold">${{ x3Item.prices[0].usdAmount }}</span>
              </div>
            </BaseButton>
          </div>
          <div class="flex-1 relative">
            <BaseButton
              v-if="x5Item"
              style-type="primary-pink"
              class="w-full !px-0"
              :button-class="hasDefaultBOGO ? 'default-BOGO-background-buttons border-0' : (isDefaultBOGX ? 'default-BOGX-background-buttons border-0' : '')"
              :text-class="hasDefaultBOGO || isDefaultBOGX ? 'text-[#1D1D1B]':''"
              @click="selectedEcommerceItem = x5Item"
            >
              <div class="flex items-center gap-x-2 relative max-[390px]:text-base max-[390px]:gap-x-1.5">
                <span
                  class="lowercase"
                  :class="(hasBOGO || isBOGX) && 'text-base line-through text-tropee-black/[66%]'"
                >x5</span>
                <span
                  v-if="hasBOGO || isBOGX"
                  class="absolute left-0 -rotate-[23deg] black-stroke-golden-text text-lg bottom-0 translate-y-1/2 lowercase"
                >x{{ hasBOGO ? 10 : 5 * saleValue }}</span>
                <div class="w-px h-4 bg-tropee-black/[.24]" />
                <span class="!font-semibold">${{ x5Item.prices[0].usdAmount }}</span>
              </div>
            </BaseButton>
            <img
              class="absolute -right-4 -top-4 object-cover size-10 rotate-[23deg]"
              src="@/assets/images/badge-20.png"
            >
          </div>
        </div>
      </template>
      <MysteryBoxPurchaseEcommerceItem
        v-else
        :box-item="item"
        :ecommerce-item="selectedEcommerceItem as EcommerceItem"
        @on-purchase="$emit('onPurchase', selectedEcommerceItem as EcommerceItem)"
      />
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>